/**
 * Typing indicator hook — Supabase Realtime Presence.
 *
 * Each conversation gets a dedicated Presence channel. When the local
 * user types, their presence state is updated with `typing: true` along
 * with their `displayName`. After 3 seconds of inactivity the state
 * resets to `typing: false`. The typing row is pruned automatically when
 * the WebSocket drops (Presence ephemeral semantics).
 *
 * Returns `typingUserName` so callers can render "<name> is typing…"
 * directly without re-querying the conversation cache for the
 * counterparty's profile.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { supabase } from '../supabase'
import type { RealtimeChannel } from '@supabase/supabase-js'

const TYPING_TIMEOUT_MS = 3000

interface PresencePayload {
  typing: boolean
  /**
   * Display name of the typing user. Tracked in Presence so a recipient
   * UI can render "Alice is typing" without an extra profile lookup —
   * particularly important if the counterparty isn't in the local
   * conversations cache yet (deep-linked into a fresh conversation).
   */
  displayName: string | null
}

interface TypingIndicatorOptions {
  /**
   * The local user's display name. Tracked in our outgoing Presence
   * payload so the counterparty's UI can render our name. Pass null if
   * unknown — the recipient just sees a generic "is typing".
   */
  displayName?: string | null
}

interface TypingIndicatorResult {
  /** True if any participant other than us is typing right now. */
  isOtherTyping: boolean
  /**
   * The display name of the typing user (first non-self typer found),
   * or null if nobody else is typing or they didn't track a name.
   */
  typingUserName: string | null
  setTyping: (typing: boolean) => void
}

export function useTypingIndicator(
  conversationId: string | undefined,
  userId: string,
  options: TypingIndicatorOptions = {},
): TypingIndicatorResult {
  const { displayName: localDisplayName = null } = options
  const [isOtherTyping, setIsOtherTyping] = useState(false)
  const [typingUserName, setTypingUserName] = useState<string | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTypingRef = useRef(false)

  // Keep the latest local displayName in a ref so re-tracking from the
  // setTyping callback always uses the freshest name without resubscribing.
  const localNameRef = useRef(localDisplayName)
  useEffect(() => {
    localNameRef.current = localDisplayName

    // If the channel is already subscribed and our typing state is true,
    // re-track to push the updated name into Presence immediately.
    if (channelRef.current && isTypingRef.current) {
      void channelRef.current.track({
        typing: true,
        displayName: localDisplayName,
      } satisfies PresencePayload)
    }
  }, [localDisplayName])

  useEffect(() => {
    if (!conversationId || !userId) return

    const channel = supabase.channel(`typing:${conversationId}`, {
      config: { presence: { key: userId } },
    })

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState<PresencePayload>()
        let otherTyping = false
        let otherName: string | null = null
        for (const [key, presences] of Object.entries(state)) {
          if (key === userId) continue
          const typer = presences.find((p) => p.typing === true)
          if (typer) {
            otherTyping = true
            otherName = typer.displayName ?? null
            break
          }
        }
        setIsOtherTyping(otherTyping)
        setTypingUserName(otherName)
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            typing: false,
            displayName: localNameRef.current,
          } satisfies PresencePayload)
        }
      })

    channelRef.current = channel

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      void supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [conversationId, userId])

  const setTyping = useCallback((typing: boolean) => {
    if (!channelRef.current) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (typing) {
      // Debounce: only push an update if we weren't already typing.
      if (!isTypingRef.current) {
        isTypingRef.current = true
        void channelRef.current.track({
          typing: true,
          displayName: localNameRef.current,
        } satisfies PresencePayload)
      }

      // Auto-reset after inactivity.
      timeoutRef.current = setTimeout(() => {
        isTypingRef.current = false
        if (channelRef.current) {
          void channelRef.current.track({
            typing: false,
            displayName: localNameRef.current,
          } satisfies PresencePayload)
        }
      }, TYPING_TIMEOUT_MS)
    } else if (isTypingRef.current) {
      // Explicit stop (e.g., message sent).
      isTypingRef.current = false
      void channelRef.current.track({
        typing: false,
        displayName: localNameRef.current,
      } satisfies PresencePayload)
    }
  }, [])

  return { isOtherTyping, typingUserName, setTyping }
}
